import { getXmlSlashEdit, XML_LANGUAGE_ID, type XmlSlashEdit } from '@lved/shared-core';
import { FrontendApplicationContribution } from '@theia/core/lib/browser';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import { inject, injectable } from '@theia/core/shared/inversify';
import { EditorManager, EditorWidget } from '@theia/editor/lib/browser';
import * as monaco from '@theia/monaco-editor-core';
import type { MonacoBackedEditor } from './types';

@injectable()
export class XmlSlashEditingContribution implements FrontendApplicationContribution {
    @inject(EditorManager)
    protected readonly editorManager!: EditorManager;

    protected readonly toDispose: DisposableCollection = new DisposableCollection();
    protected readonly editorDisposables: WeakMap<EditorWidget, DisposableCollection> = new WeakMap();

    onStart(): void {
        this.toDispose.push(this.editorManager.onCreated((widget: EditorWidget) => this.attachSlashHandler(widget)));

        for (const widget of this.editorManager.all) {
            this.attachSlashHandler(widget);
        }
    }

    onStop(): void {
        this.toDispose.dispose();
    }

    protected attachSlashHandler(widget: EditorWidget): void {
        if (this.editorDisposables.has(widget)) return;

        const editor: monaco.editor.IStandaloneCodeEditor | undefined = (
            widget.editor as unknown as MonacoBackedEditor
        ).getControl?.();
        if (!editor) return;
        const disposables: DisposableCollection = new DisposableCollection();

        disposables.push(editor.onKeyDown((event: monaco.IKeyboardEvent) => this.handleKeyDown(event, editor)));

        const dispose = (): void => {
            disposables.dispose();
            this.editorDisposables.delete(widget);
        };

        widget.disposed.connect(dispose);
        disposables.push({
            dispose: (): void => {
                widget.disposed.disconnect(dispose);
            }
        });

        this.editorDisposables.set(widget, disposables);
        this.toDispose.push(disposables);
    }

    protected handleKeyDown(event: monaco.IKeyboardEvent, editor: monaco.editor.IStandaloneCodeEditor): void {
        if (event.browserEvent.key !== '/') return;

        const model: monaco.editor.ITextModel | null = editor.getModel();
        const position: monaco.Position | null = editor.getPosition();
        if (!model || !position || model.getLanguageId() !== XML_LANGUAGE_ID) return;

        const cursorOffset: number = model.getOffsetAt(position);
        const lineStartOffset: number = model.getOffsetAt({ lineNumber: position.lineNumber, column: 1 });
        const edit: XmlSlashEdit | undefined = getXmlSlashEdit({
            textUntilCursor: model.getValue().substring(0, cursorOffset),
            textAfterCursorOnLine: model.getLineContent(position.lineNumber).substring(position.column - 1),
            currentLineContent: model.getLineContent(position.lineNumber),
            cursorOffset,
            lineStartOffset
        });
        if (!edit) return;

        const startPosition: monaco.Position = model.getPositionAt(edit.startOffset);
        const endPosition: monaco.Position = model.getPositionAt(edit.endOffset);

        event.preventDefault();
        editor.executeEdits(edit.source, [
            {
                range: new monaco.Range(
                    startPosition.lineNumber,
                    startPosition.column,
                    endPosition.lineNumber,
                    endPosition.column
                ),
                text: edit.text
            }
        ]);
    }
}
